import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface CartItem {
  productId: string;
  quantity: number;
}

export interface OrderItem {
  productId: string;
  title: string;
  price: number;
  quantity: number;
}

export interface Order {
  id: string;
  items: OrderItem[];
  total: number;
  date: string;
  status: '결제 완료';
}

export interface User {
  id: string;
  name: string;
  email: string;
  provider: string;
  role: 'member' | 'seller' | 'admin';
}

interface AppState {
  user: User | null;
  authLoading: boolean;
  cart: CartItem[];
  wishlist: string[];
  orders: Order[];
  addToCart: (productId: string) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  toggleWishlist: (productId: string) => void;
  signUpWithEmail: (
    email: string,
    password: string,
    name: string,
  ) => Promise<{ error: string | null }>;
  signInWithEmail: (
    email: string,
    password: string,
  ) => Promise<{ error: string | null }>;
  signInWithProvider: (provider: 'google' | 'kakao') => Promise<void>;
  signOut: () => Promise<void>;
  placeOrder: (items: OrderItem[], total: number) => Order;
}

const AppContext = createContext<AppState | null>(null);

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function userFromSession(session: Session | null): User | null {
  if (!session?.user) return null;
  const meta = session.user.user_metadata ?? {};
  const provider = session.user.app_metadata?.provider ?? 'email';
  return {
    id: session.user.id,
    name: meta.name ?? meta.full_name ?? session.user.email?.split('@')[0] ?? '회원',
    email: session.user.email ?? '',
    provider: provider === 'email' ? '이메일' : provider,
    role: 'member',
  };
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState<CartItem[]>(() => load<CartItem[]>('app_cart', []));
  const [wishlist, setWishlist] = useState<string[]>(() => load<string[]>('app_wishlist', []));
  const [orders, setOrders] = useState<Order[]>(() => load<Order[]>('app_orders', []));

  const applyProfileRole = (nextUser: User | null) => {
    if (!nextUser) return;
    supabase
      .from('profiles')
      .select('role, name')
      .eq('id', nextUser.id)
      .single()
      .then(({ data }) => {
        if (!data) return;
        setUser((prev) =>
          prev && prev.id === nextUser.id
            ? { ...prev, role: data.role ?? 'member', name: data.name ?? prev.name }
            : prev,
        );
      });
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const nextUser = userFromSession(data.session);
      setUser(nextUser);
      setAuthLoading(false);
      applyProfileRole(nextUser);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = userFromSession(session);
      setUser(nextUser);
      setAuthLoading(false);
      applyProfileRole(nextUser);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    localStorage.setItem('app_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('app_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    localStorage.setItem('app_orders', JSON.stringify(orders));
  }, [orders]);

  const addToCart = useCallback((productId: string) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.productId === productId);
      if (existing) {
        return prev.map((i) =>
          i.productId === productId ? { ...i, quantity: i.quantity + 1 } : i,
        );
      }
      return [...prev, { productId, quantity: 1 }];
    });
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart((prev) => prev.filter((i) => i.productId !== productId));
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) =>
      prev.map((i) => (i.productId === productId ? { ...i, quantity } : i)),
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((productId: string) => {
    setWishlist((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
    );
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string, name: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    return { error: error?.message ?? null };
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  }, []);

  const signInWithProvider = useCallback(async (provider: 'google' | 'kakao') => {
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin,
        // 카카오 개인 개발자 앱은 이메일 권한이 없어 account_email 스코프를 요청하면 실패한다.
        ...(provider === 'kakao' ? { scopes: 'profile_nickname' } : {}),
      },
    });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const placeOrder = useCallback((items: OrderItem[], total: number): Order => {
    const order: Order = {
      id: `ORD-${Date.now()}`,
      items,
      total,
      date: new Date().toISOString(),
      status: '결제 완료',
    };
    setOrders((prev) => [order, ...prev]);

    if (user) {
      supabase
        .from('orders')
        .insert({ buyer_id: user.id, total, status: 'paid' })
        .select()
        .single()
        .then(({ data, error }) => {
          if (error || !data) return;
          void supabase.from('order_items').insert(
            items.map((item) => ({
              order_id: data.id,
              product_id: item.productId,
              title: item.title,
              price: item.price,
              quantity: item.quantity,
            })),
          );
        });
    }

    return order;
  }, [user]);

  const contextValue = useMemo(
    () => ({
      user,
      authLoading,
      cart,
      wishlist,
      orders,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      toggleWishlist,
      signUpWithEmail,
      signInWithEmail,
      signInWithProvider,
      signOut,
      placeOrder,
    }),
    [
      user,
      authLoading,
      cart,
      wishlist,
      orders,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      toggleWishlist,
      signUpWithEmail,
      signInWithEmail,
      signInWithProvider,
      signOut,
      placeOrder,
    ],
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useApp must be used within AppProvider');
  }
  return ctx;
}
